import { trpcClient } from "@tokenterminal/core/utils/trpc"

import { PageMeta } from "components/PageMeta"
import { Tracking } from "components/Tracking"
import { shouldSkipStaticBuild } from "helpers/config"
import { V3StaticLayout } from "layouts/v3Static"
import { type PageType } from "modules/analytics/types"
import { MarketSectorDetailPage } from "modules/market-sectors/DetailPage/MarketSectorDetailPage"
import { useMarketSector } from "modules/market-sectors/hooks/useMarketSector"
import { useRouter } from "next/router"
import { type MarketSectorDetails } from "utils/trpc"
import type { GetStaticPaths, GetStaticProps } from "next"

type Props = {
  description: string
  marketSlug: string
  marketSector: MarketSectorDetails
  name: string
  projectCount: number
}

function AsyncPageMeta({ slug }: { slug: string }) {
  const { data: { data: marketDetails } = { data: null } } =
    useMarketSector(slug)

  const title = `${marketDetails?.name} | Markets`

  return (
    <PageMeta meta={{ title, description: marketDetails?.description?.what }} />
  )
}

function MarketPage({ marketSlug, marketSector, description }: Props) {
  const router = useRouter()
  const slug = (router.query.marketSlug as string | undefined) ?? marketSlug

  return (
    <Tracking pageId={slug} pageType={"market-sector" as PageType}>
      <AsyncPageMeta slug={slug} />
      <MarketSectorDetailPage
        marketSector={marketSector}
        // A market sector "id" is really a slug-like string
        marketSectorId={slug}
        description={description}
        activeTabSlug="overview"
      />
    </Tracking>
  )
}

MarketPage.getLayout = V3StaticLayout

export default MarketPage

type MarketStaticParams = { marketSlug: string }

export const getStaticPaths: GetStaticPaths<MarketStaticParams> = async () => {
  if (shouldSkipStaticBuild()) {
    return {
      paths: [],
      fallback: "blocking", // always use fallback to allow page build on request
    }
  }
  const { data: markets } =
    await trpcClient.marketSectors.getMarketSectors.query()

  return {
    paths: markets.map((market) => {
      return {
        params: {
          marketSlug: `${market.slug}`,
        },
      }
    }),
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<
  Props,
  MarketStaticParams
> = async ({ params, ...rest }) => {
  if (!params?.marketSlug) {
    return {
      notFound: true,
      props: {},
    }
  }

  try {
    const { data } = await trpcClient.marketSectors.getMarketSector.query({
      market_sector_id: params.marketSlug,
    })

    return {
      props: {
        marketSlug: params.marketSlug,
        name: data.name,
        projectCount: data.projects.length,
        description: data.description.what,
        marketSector: data,
      },
    }
  } catch (err) {
    return {
      notFound: true,
      props: {},
    }
  }
}
